import {
  Tab as NovaHorizontalTab,
  TabProps as NovaTabProps,
  Tabs as NovaHorizontalTabs,
  TabsProps as NovaTabsProps,
  VerticalTab as NovaVerticalTab,
  VerticalTabProps as NovaVerticalTabProps,
  VerticalTabs as NovaVerticalTabs,
  VerticalTabsProps as NovaVerticalTabsProps,
} from '@fme-nova/components'
import { SxProps, Theme } from '@mui/material'
import { default as MuiTabBase, TabProps as MuiTabProps } from '@mui/material/Tab'
import { default as MuiTabsBase, TabsProps as MuiTabsProps } from '@mui/material/Tabs'
import React, { ReactNode, useEffect, useState } from 'react'

import { createComponentWrapperWithRef, css, styled, THEME_CONST, uiTokens } from '../../theme'
import { COLORS } from '../../theme/colors'
import { Divider } from '../Divider'

const MuiTab = styled(MuiTabBase)`
  color: ${COLORS.text.secondary};
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
  border-bottom: 2px solid transparent;
  /* stylelint-disable shorthand-property-no-redundant-values */
  padding: 9px 16px 7px 16px;
  min-height: 42px;

  &:hover {
    color: ${COLORS.primary.main};
    background-color: ${COLORS.primary.hover};
  }
  &.Mui-selected {
    color: ${COLORS.primary.main};
  }
`
const MuiHorizontalTab = styled(MuiTab)`
  &:hover {
    border-bottom: 2px solid ${COLORS.primary.main};
  }
`
const MuiVerticalTab = styled(MuiTab)`
  border-right: 2px solid transparent;

  &:hover {
    border-right: 2px solid ${COLORS.primary.main};
  }
`

const MuiStyledTabs = styled(MuiTabsBase)`
  min-height: ${THEME_CONST.tabMinHeight}px;
  max-height: ${THEME_CONST.tabMaxHeight}px;
  padding-left: ${THEME_CONST.defaultPadding}px;
  padding-right: ${THEME_CONST.defaultPadding}px;

  background-color: ${COLORS.white.main};
  .MuiTabs-indicator {
    background-color: ${COLORS.primary.main};
  }
`

const MuiStyledTabContent = styled('div', {
  shouldForwardProp: (prop) => prop !== '$orientation' && prop !== '$hasCustomSubHeader',
})<{
  $orientation?: 'horizontal' | 'vertical'
  $hasCustomSubHeader?: boolean
}>`
  padding-top: ${THEME_CONST.defaultPadding}px;
  background-color: ${COLORS.components.tab.content.background};
`

const NovaStyledTabContent = styled('div', {
  shouldForwardProp: (prop) => prop !== '$orientation' && prop !== '$hasCustomSubHeader',
})<{
  $orientation?: 'horizontal' | 'vertical'
  $hasCustomSubHeader?: boolean
}>`
  background-color: ${uiTokens.uiSurface.background};
  ${(props) =>
    props.$orientation === 'vertical' &&
    css`
      && {
        padding-top: 0;
        padding-bottom: 0;
        overflow-y: auto;
        scrollbar-gutter: stable;
        max-height: ${props.$hasCustomSubHeader
          ? `calc(100dvh - (270px + (2 * ${uiTokens.font.fontSize.heading.h5}px + ${uiTokens.spacing.padding.medium}px + ${uiTokens.spacing.padding.semiMedium}px)))`
          : 'calc(100dvh - 270px)'};
        /* scrollbar vertical tabs tab content styling */
        scrollbar-width: thin;
        scrollbar-color: rgba(0, 0, 0, 0.3) transparent;

        &::-webkit-scrollbar {
          width: 8px;
        }
        &::-webkit-scrollbar-track {
          background: transparent;
        }
        &::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.3);
          border-radius: 4px;
        }
        /* /scrollbar vertical tabs tab content styling */
      }
      &&:has(> .MuiBox-root:first-child .MuiInputLabel-root):not(:has(> .MuiBox-root:first-child .MuiCardHeader-root)) {
        padding-top: 5px;
      }
    `}
`

const StyledTabContent = createComponentWrapperWithRef(
  {
    mui: MuiStyledTabContent,
    nova: NovaStyledTabContent,
  },
  'TabContent',
)

export interface TabEntity {
  label: string | ReactNode
  content: React.ReactNode
  isCustom?: boolean
}

interface TabContentProps {
  children?: React.ReactNode
  index: number
  value: number
  isCustom?: boolean
  orientation?: 'horizontal' | 'vertical'
  hasCustomSubHeader?: boolean
}

function TabContent(props: TabContentProps) {
  const { children, value, index, isCustom, ...other } = props

  const classNames = ['tab-content']
  if (isCustom) {
    classNames.push('tab-content--custom')
  }
  if (value === index) {
    classNames.push('tab-content--active')
  }

  return (
    <StyledTabContent
      className={classNames.join(' ')}
      data-test-id="tab-content"
      role="tabpanel"
      hidden={value !== index}
      id={`tab-content-${index}`}
      aria-labelledby={`tab-content-${index}`}
      $orientation={props.orientation}
      $hasCustomSubHeader={props.hasCustomSubHeader}
      {...other}
    >
      {value === index && children}
    </StyledTabContent>
  )
}

const TabHorizontal = createComponentWrapperWithRef<MuiTabProps | NovaTabProps>(
  {
    mui: MuiHorizontalTab,
    nova: NovaHorizontalTab,
  },
  'Tab',
)

const TabsHorizontal = createComponentWrapperWithRef<MuiTabsProps | NovaTabsProps>(
  {
    mui: MuiStyledTabs,
    nova: styled(NovaHorizontalTabs)`
      padding-left: ${uiTokens.spacing.padding.large}px;
      padding-right: ${uiTokens.spacing.padding.large}px;
      background-color: ${uiTokens.color.basic.white};
    `,
  },
  'Tabs',
)

const TabVertical = createComponentWrapperWithRef<MuiTabProps | NovaVerticalTabProps>(
  {
    mui: MuiVerticalTab as unknown as React.ComponentType<MuiTabProps | NovaVerticalTabProps>,
    nova: NovaVerticalTab as unknown as React.ComponentType<NovaTabProps | NovaVerticalTabProps>,
  },
  'Tab',
)

const TabsVertical = createComponentWrapperWithRef<MuiTabsProps | NovaVerticalTabsProps>(
  {
    mui: MuiStyledTabs,
    nova: NovaVerticalTabs as unknown as React.ComponentType<MuiTabsProps | NovaVerticalTabsProps>,
    muiProps: {
      orientation: 'vertical',
    },
  },
  'Tabs',
)

export function Tabs(props: {
  childs: TabEntity[]
  onChange?: (newValue: number) => void
  selectedTab?: number
  startingTab?: number
  orientation?: 'horizontal' | 'vertical'
  hasCustomSubHeader?: boolean
  sx?: SxProps<Theme>
}) {
  const [internalSelected, setInternalSelected] = useState(props.startingTab ?? 0)
  const selected = props.selectedTab ?? internalSelected

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setInternalSelected(newValue)
    if (props.onChange) {
      props.onChange(newValue)
    }
  }

  useEffect(() => {
    if (!props.selectedTab) {
      return
    }

    if (props.selectedTab < 0) {
      if (props.onChange) {
        props.onChange(0)
      }
    }

    if (props.selectedTab > props.childs.length - 1) {
      if (props.onChange) {
        props.onChange(props.childs.length - 1)
      }
    }
  }, [props.selectedTab])
  const TabsComponent = props.orientation === 'vertical' ? TabsVertical : TabsHorizontal

  const TabComponent = props.orientation === 'vertical' ? TabVertical : TabHorizontal

  return (
    <>
      <TabsComponent data-test-id="tabs" value={selected} onChange={handleChange} sx={props.sx}>
        {props.childs.map((e, idx) => {
          return <TabComponent disableRipple data-test-id="tab-item" key={idx} label={e.label}></TabComponent>
        })}
      </TabsComponent>
      {props.orientation !== 'vertical' && <Divider />}
      {props.childs.map((e, idx) => {
        return (
          <TabContent
            key={idx}
            index={idx}
            value={selected}
            isCustom={e.isCustom}
            orientation={props.orientation}
            hasCustomSubHeader={props.hasCustomSubHeader}
          >
            {e.content}
          </TabContent>
        )
      })}
    </>
  )
}
